import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { SessionProvider, useSession } from "./lib/session";
import { configError } from "./lib/supabase";
import SetupNeeded from "./screens/SetupNeeded";
import Layout from "./components/Layout";
import { Loading } from "./components/ui";
import SignIn from "./screens/SignIn";
import Landing from "./screens/Landing";
import CreateSociety from "./screens/CreateSociety";
import Welcome from "./screens/Welcome";
import BrandShell from "./components/BrandShell";
import Today from "./screens/Today";
import Rounds from "./screens/Rounds";
import Pick from "./screens/Pick";
import Members from "./screens/Members";
import JoinSociety from "./screens/JoinSociety";
import Account from "./screens/Account";
import Platform from "./screens/Platform";
import SocietySettings from "./screens/SocietySettings";
import Settings from "./screens/Settings";
import CompetitionField from "./screens/CompetitionField";
import Swindle from "./screens/Swindle";
import SwindleSettings from "./screens/SwindleSettings";
import Courses from "./screens/Courses";
import Competitions from "./screens/Competitions";
import CompetitionNew from "./screens/CompetitionNew";
import Round from "./screens/Round";
import TeeTimes from "./screens/TeeTimes";
import CompetitionBoard from "./screens/CompetitionBoard";
import ScoreEntry from "./screens/ScoreEntry";

function Gate() {
  const { loading, user, societies, society } = useSession();

  if (loading) return <Loading />;
  // Signed out, the front page is the front page. Sign-in is a destination
  // you choose, not a wall you hit — a visitor who's never heard of this
  // should be able to read what it is first.
  if (!user) {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    );
  }
  if (societies.length === 0) return <CreateSociety />;   // nowhere to go yet
  if (!society) {
    // The account page is reachable from the welcome screen, so it can't
    // live only inside the society shell.
    return (
      <Routes>
        {/* reachable from the welcome screen, so it can't live only inside
            the society shell */}
        <Route path="/account" element={<BrandShell><Account /></BrandShell>} />
        <Route path="/join" element={<BrandShell><JoinSociety /></BrandShell>} />
        <Route path="*" element={<Welcome />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Society level: set up once a season, then left alone. These sit
            behind the avatar rather than taking a tab each. */}
        <Route index element={<Pick />} />
        <Route path="/competitions" element={<Competitions />} />
        <Route path="/competitions/new" element={<CompetitionNew />} />
        <Route path="/members" element={<Members />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/join" element={<JoinSociety />} />
        <Route path="/account" element={<Account />} />
        <Route path="/platform" element={<Platform />} />
        <Route path="/society" element={<Settings />} />
        <Route path="/society-settings" element={<SocietySettings />} />
        <Route path="/settings" element={<Navigate to="/society" replace />} />

        {/* Inside a competition. The id is in the path, so every screen
            below knows its context without any shared state. */}
        <Route path="/c/:id" element={<Navigate to="today" replace />} />
        <Route path="/c/:id/today" element={<Today />} />
        <Route path="/c/:id/rounds" element={<Rounds />} />
        <Route path="/c/:id/r/:roundId" element={<Round />} />
        <Route path="/c/:id/r/:roundId/tee-times" element={<TeeTimes />} />
        <Route path="/c/:id/board" element={<CompetitionBoard />} />
        <Route path="/c/:id/swindle" element={<Swindle />} />
        <Route path="/c/:id/swindle/settings" element={<SwindleSettings />} />
        <Route path="/c/:id/field" element={<CompetitionField />} />

        <Route path="/cards/:entryId" element={<ScoreEntry />} />

        {/* the old shapes, so anything already shared still lands */}
        <Route path="/competitions/:id" element={<Redirect to="today" />} />
        <Route path="/competitions/:id/board" element={<Redirect to="board" />} />
        <Route path="/competitions/:id/swindle" element={<Redirect to="swindle" />} />
        <Route path="/competitions/:id/field" element={<Redirect to="field" />} />
        <Route path="/rounds/:id" element={<RoundRedirect />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Old /competitions/:id/x links land on the new /c/:id/x. */
function Redirect({ to }) {
  const { id } = useParams();
  return <Navigate to={`/c/${id}/${to}`} replace />;
}

/** A round link has no competition in it, so look it up and move on. */
function RoundRedirect() {
  const { id } = useParams();
  const [to, setTo] = React.useState(null);
  React.useEffect(() => {
    supabase.from("rounds").select("competition_id").eq("id", id).maybeSingle()
      .then(({ data }) => setTo(data ? `/c/${data.competition_id}/r/${id}` : "/"));
  }, [id]);
  return to ? <Navigate to={to} replace /> : null;
}

export default function App() {
  // Check this before anything else: without settings there is nothing to
  // sign in to, and a blank page tells you nothing.
  if (configError) return <SetupNeeded />;

  return (
    <BrowserRouter>
      <SessionProvider>
        <Gate />
      </SessionProvider>
    </BrowserRouter>
  );
}
